import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useError } from '../contexts/ErrorContext';
import Feed from '../cards/videos/Feed';
import { api_post } from '@lf/api-client/lfApi';
import { FunLoader } from '@lf/ui-primitives';
import SubscriptionActionBanner from '../cards/videos/SubscriptionActionBanner';
import { useFireworks } from '../lib/videos/useFireworks';
import { useFeedData } from '../lib/videos/useFeedData';

function VideosPage({ profile, studyPlans, subscriptionAction, isLinked }) {
    const [studyPlanId, setStudyPlanId] = useState(null);
    const [, setKeywords] = useState('');

    const { showFireworks, setShowFireworks, fireworksContainerRef } = useFireworks();

    const { setError } = useError();

    const {
        feedMetadata,
        setFeedMetadata,
        feedItemsResp,
        setFeedItemsResp,
        recommendedPath,
        setRecommendedPath,
        debugFeedData,
        setDebugFeedData,
        isLoadingItems,
        isRecommendedPathLoading,
        feedItems,
        showFunLoader,
        fetchFeed,
        fetchFeedItems,
        fetchRecommendedPath,
        fetchDebugFeedData,
        DEBUG_MODE_ENABLED,
    } = useFeedData({ setError });

    const location = useLocation();
    const navigate = useNavigate();

    const onFeedPrev = async (studyPlanId) => {
        setRecommendedPath(null);
        setFeedItemsResp(null);

        const response = await api_post(
            '/feed/prev',
            { lesson_plan_id: studyPlanId },
            setError,
            90000,
        );

        if (response !== null) {
            const res = response.data.items[0];
            setFeedMetadata(res);

            fetchFeedItems(studyPlanId);
            // Fetch recommended path for the new lesson (lesson-keyed; the
            // per-topic view is derived client-side from video_topic_map).
            if (res?.current_position?.lesson_id) {
                fetchRecommendedPath(
                    studyPlanId,
                    res.current_position.lesson_id,
                );
            }
        }
    };

    const onFeedNext = async (studyPlanId, showFireworks = true) => {
        const startTime = Date.now();
        setShowFireworks(showFireworks);
        setRecommendedPath(null);
        setFeedItemsResp(null);

        const response = await api_post(
            '/feed/next',
            { lesson_plan_id: studyPlanId },
            setError,
            90000,
        );

        let remainingTime = 0;
        if (response !== null) {
            const res = response.data.items[0];
            setFeedMetadata(res);

            fetchFeedItems(studyPlanId);
            if (res?.current_position?.lesson_id) {
                fetchRecommendedPath(
                    studyPlanId,
                    res.current_position.lesson_id,
                );
            }

            const responseTime = Date.now();
            remainingTime = Math.max(2000, 5000 - (responseTime - startTime));
        }

        setTimeout(() => {
            setShowFireworks(false);
        }, remainingTime);
    };

    const onFeedNextTopic = async (studyPlanId) => {
        setRecommendedPath(null);
        setFeedItemsResp(null);

        const response = await api_post(
            '/feed/next-topic',
            { lesson_plan_id: studyPlanId },
            setError,
            90000,
        );

        if (response !== null) {
            const res = response.data.items[0];
            setFeedMetadata(res);

            fetchFeedItems(studyPlanId);
            if (res?.current_position?.lesson_id) {
                fetchRecommendedPath(
                    studyPlanId,
                    res.current_position.lesson_id,
                );
            }
        }
    };

    const onVideoSelect = async (video, studyPlanId, currentPosition, options = {}) => {
        const lessonId = currentPosition.lesson_id;
        const topicId = currentPosition.topic_id;
        const params = new URLSearchParams();
        params.set('vid', video.id.videoId);
        params.set('cid', video.snippet.channelId);
        params.set('spid', studyPlanId);
        params.set('lid', lessonId);
        params.set('tid', topicId);
        // Surface that initiated the play; used by VideoDetail to fire
        // `video_played` with the right source. Default to `study_plan`
        // because this page only renders inside a study plan context.
        params.set('src', options.source || 'study_plan');
        if (options.recPosition !== undefined && options.recPosition !== null) {
            params.set('recpos', String(options.recPosition));
        }
        if (video.snippet?.title) {
            params.set('title', video.snippet.title);
        }
        if (video.snippet?.channelTitle) {
            params.set('ctitle', video.snippet.channelTitle);
        }
        const thumbUrl =
            video.snippet?.thumbnails?.high?.url ||
            video.snippet?.thumbnails?.medium?.url ||
            video.snippet?.thumbnails?.default?.url ||
            '';
        if (thumbUrl) {
            params.set('thumb', thumbUrl);
        }
        navigate(`/video?${params.toString()}`);
    };

    const onStudyPlanSelect = async (studyPlanId) => {
        navigate(`/studyPlan/${studyPlanId}`);
    };

    // Poll for recommended path until it is ready (or errors).
    const lessonId = feedMetadata?.current_position?.lesson_id;
    const topicId = feedMetadata?.current_position?.topic_id;

    useEffect(() => {
        if (!DEBUG_MODE_ENABLED || !studyPlanId || !lessonId || !topicId) {
            setDebugFeedData(null);
            return;
        }
        const allVideoIds = (feedItemsResp?.items || [])
            .map((item) => item?.id?.videoId)
            .filter(Boolean);
        const recommendedVideoIds = (recommendedPath?.recommended_videos || [])
            .map((item) => item?.video_id)
            .filter(Boolean);

        if (allVideoIds.length === 0 && recommendedVideoIds.length === 0) {
            setDebugFeedData(null);
            return;
        }

        fetchDebugFeedData(
            studyPlanId,
            lessonId,
            topicId,
            allVideoIds,
            recommendedVideoIds,
        );
    }, [
        fetchDebugFeedData,
        feedItemsResp?.items,
        lessonId,
        recommendedPath?.recommended_videos,
        studyPlanId,
        topicId,
    ]);

    useEffect(() => {
        if (
            studyPlanId &&
            lessonId &&
            (
                !recommendedPath ||
                recommendedPath.status === 'computing' ||
                recommendedPath.status === 'pending'
            )
        ) {
            const interval = setInterval(() => {
                if (isRecommendedPathLoading) return;
                fetchRecommendedPath(studyPlanId, lessonId);
            }, 15000);
            return () => clearInterval(interval);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        recommendedPath?.status,
        studyPlanId,
        lessonId,
        isRecommendedPathLoading,
        fetchRecommendedPath,
    ]);

    useEffect(() => {
        const keywords = new URLSearchParams(location.search).get('k');
        if (keywords)
            setKeywords(keywords);

        const spid = new URLSearchParams(location.search).get('sp');
        if (spid)
            setStudyPlanId(spid);

        if (profile)
            fetchFeed(spid, keywords);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile, location.search]);

    return <div>
        {showFireworks && (
            <div
                data-testid="fireworks-container"
                ref={fireworksContainerRef}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 9999
                }}
            />
        )}
        {
            subscriptionAction.action !== 'none' && <SubscriptionActionBanner subscriptionAction={subscriptionAction} isLinked={isLinked} />
        }
        {
        showFunLoader || !studyPlans || studyPlans.length === 0
            ? <div className="mt-8"><FunLoader /></div>
            : (studyPlanId
                ? <Feed
                    videos={[feedItems]}
                    recommendedPath={recommendedPath}
                    isRecommendedPathLoading={isRecommendedPathLoading}
                    isItemsLoading={isLoadingItems}
                    debugMode={DEBUG_MODE_ENABLED}
                    debugFeedData={debugFeedData}
                    onVideoSelect={onVideoSelect}
                    onStudyPlanSelect={onStudyPlanSelect}
                    onPrev={onFeedPrev}
                    onNext={onFeedNext}
                    onNextTopic={onFeedNextTopic} />
                : <Navigate to={`/feed?sp=${studyPlans[0].id}`} />
            )
        }
    </div>;
}

export default VideosPage;
